import { useQuery } from '@tanstack/react-query';

import { api } from '../../lib/api';
import { setAppTimeZone } from '../../lib/dates';
import type {
  DailyAgenda,
  Page,
  ProjectCard,
  ProjectStatus,
  ReminderDto,
  VentureDto,
} from '../../lib/dto';
import { qk } from '../../lib/query-keys';

/**
 * قراءات شاشة «اليوم». كلها عبر مصنع المفاتيح `qk` وحده (§1.3) كي تشملها إبطالات
 * SSE، وكلها قراءة محضة.
 */

/** حالاتُ التذكيرات التي يقرؤها المجموع الواحد. */
export const REMINDER_POOL_STATUSES = ['pending', 'snoozed', 'sent'] as const;

export function useAgendaQuery(enabled = true) {
  return useQuery({
    queryKey: qk.agendaToday(),
    queryFn: async (): Promise<DailyAgenda> => {
      const agenda = await api.get<DailyAgenda>('/views/agenda');
      // منطقة المالك تُثبَّت من حمولة الأجندة وحدها — المعرّف لا يُكتب في العميل (D30).
      setAppTimeZone(agenda.timezone);
      return agenda;
    },
    enabled,
  });
}


/**
 * مشاريع معرض الرئيسية وحزام المؤشرات (المرحلة أ2).
 *
 * المفتاح من `qk.projects` — لا مفتاح يدويّ: عائلة `project` في خريطة إبطال SSE
 * تُبطل البادئة العريضة `['projects']`، فيحيا المعرض مجانًا. ومفتاحٌ مكتوب بيدٍ
 * هنا كان يخرج من نطاق الإبطال **بصمت**: الشبكة تُرسم مرة عند أول تحميل ثم لا
 * تتحدّث أبدًا (فخّ §6.8 المسجَّل).
 *
 * والحمولة نفسها تُغذّي المؤشرات — فلا نداء ثانٍ لأجل أربعة أرقام.
 */
export const HOME_PROJECT_STATUSES: readonly ProjectStatus[] = ['idea', 'active', 'waiting'];

export function useHomeProjectsQuery() {
  const filters = { status: HOME_PROJECT_STATUSES } as const;
  return useQuery({
    queryKey: qk.projects(filters),
    queryFn: () =>
      api.get<Page<ProjectCard>>('/projects', {
        query: { status: HOME_PROJECT_STATUSES.join(','), limit: 50 },
      }),
  });
}

/** ألوان المظلّات لشريط بطاقة المشروع — تتغيّر نادرًا فتُبقى طازجة خمس دقائق. */
export function useVentureColorsQuery() {
  return useQuery({
    queryKey: qk.ventures(),
    queryFn: () => api.get<Page<VentureDto>>('/ventures'),
    staleTime: 5 * 60_000,
    select: (page): Readonly<Record<string, string>> =>
      Object.fromEntries(page.items.map((venture) => [venture.id, venture.color])),
  });
}


/**
 * مجموع التذكيرات الفعّالة: يخدم عنوان سبب `REMINDER_UNHANDLED` **و** يوفّر
 * `revision` لأزرار التذكير (الأجندة عرض مشتق بلا revision — قرار D70).
 */
export function useReminderPoolQuery(enabled: boolean) {
  return useQuery({
    queryKey: qk.reminders(REMINDER_POOL_STATUSES),
    queryFn: () =>
      api.get<Page<ReminderDto>>('/reminders', {
        query: { status: REMINDER_POOL_STATUSES.join(','), limit: 100 },
      }),
    enabled,
  });
}

/* ── حل عناوين كيانات الانتباه ──────────────────────────────────────────────
 * `GET /attention` يعيد `reason + entity_type + entity_id + since` بلا عنوان
 * (data-model §7.7). العنوان يُقرأ من الكيان نفسه بمفاتيح التفصيل القانونية —
 * فلا مفتاح قائمة يُسمَّم بحمولة جزئية، والـ revision يصل مجانًا مع العنوان.
 * ─────────────────────────────────────────────────────────────────────────── */

export interface AttentionEntity {
  readonly title: string;
  /** null لكيان بلا عمود revision (agent_job) — فلا ترويسة If-Match له. */
  readonly revision: number | null;
  readonly waitingOn: string | null;
  readonly projectId: string | null;
}

export type AttentionEntityMap = ReadonlyMap<string, AttentionEntity>;


